import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { SortComment } from '~/components/Comment/CommentInner/CommentInner';
import {
  CreateCommentRequest,
  DeleteCommentRequest,
  EditCommentRequest,
  GetCommentData,
  GetCommentsResponse,
  GetPersonaDetailsRequest,
  GetPersonaDetailsResponse,
  LikeCommentRequest,
} from '~/types';
import { useUserStore } from '~/store';
import characterKey from '../character/characterKey';
import commentKey from './commentKey';

export type MutationType = 'save' | 'delete';

const useUpdateCommentList = () => {
  const name = useUserStore((state) => state.name);
  const queryClient = useQueryClient();

  const updatePersonaDetailsTotalComment = (
    request: GetPersonaDetailsRequest,
    updateType: 'create' | 'delete',
  ) => {
    const prev = queryClient.getQueryData<GetPersonaDetailsResponse>(
      characterKey.getPersonaDetails({
        character_id: request.character_id,
        // type: request.type,
      }),
    );
    if (prev && prev.data) {
      queryClient.setQueryData(
        characterKey.getPersonaDetails({
          character_id: request.character_id,
          // type: request.type,
        }),
        {
          ...prev,
          data: {
            ...prev.data,
            comment: {
              ...prev.data.comment,
              total:
                updateType === 'create'
                  ? prev.data.comment.total + 1
                  : prev.data.comment.total - 1,
            },
          },
        },
      );
    }
  };

  const updateCommentList = (
    request: CreateCommentRequest,
    sort_by: SortComment,
  ) => {
    const queryKey = commentKey.getCommentList({
      character_id: request.character_id,
      sort_by,
    });

    const prev =
      queryClient.getQueryData<InfiniteData<GetCommentsResponse>>(queryKey);

    if (prev) {
      const newCommentData: GetCommentData = {
        comment_id: Date.now(), // 숫자로 변경
        writer: name,
        content: request.content,
        created_time: new Date().toISOString(),
        like_count: 0,
        is_liked: false,
        is_writer: true,
      };

      // 첫 번째 페이지에 새로운 댓글 추가
      const updatedPages = prev.pages.map((page, index) => {
        if (index === 0 && page && page.data) {
          // 페이지가 성공적인 응답인지 확인
          return {
            ...page,
            data: [newCommentData, ...page.data],
            total: page.total + 1,
          };
        }
        return page;
      });

      const updatedComments: InfiniteData<GetCommentsResponse> = {
        ...prev,
        pages: updatedPages,
      };

      // 기존 댓글 리스트에 새로운 댓글을 추가
      queryClient.setQueryData(queryKey, updatedComments);
    }
  };

  const deleteCommentList = (request: DeleteCommentRequest) => {
    const updateCommentCache = (sortOrder: SortComment) => {
      const queryKey = commentKey.getCommentList({
        character_id: request.character_id,
        sort_by: sortOrder,
      });

      const prev =
        queryClient.getQueryData<InfiniteData<GetCommentsResponse>>(queryKey);

      if (prev) {
        const updatedPages = prev.pages.map((page) => {
          if (page && page.data) {
            return {
              ...page,
              data: page.data.filter(
                (comment) => comment.comment_id !== request.comment_id,
              ),
              total: page.total - 1,
            };
          }
          return page;
        });

        const updatedComments: InfiniteData<GetCommentsResponse> = {
          ...prev,
          pages: updatedPages,
        };

        queryClient.setQueryData(queryKey, updatedComments);
      }
    };

    updateCommentCache('top');

    if (
      queryClient.getQueryData(
        commentKey.getCommentList({
          character_id: request.character_id,
          sort_by: 'newest',
        }),
      )
    ) {
      updateCommentCache('newest');
    }
  };

  const editCommentList = (request: EditCommentRequest) => {
    const updateCommentCache = (sortOrder: SortComment) => {
      const queryKey = commentKey.getCommentList({
        character_id: request.character_id,
        sort_by: sortOrder,
      });

      const prev =
        queryClient.getQueryData<InfiniteData<GetCommentsResponse>>(queryKey);

      if (prev) {
        // 해당 댓글 ID를 가진 댓글을 찾아 내용을 수정
        const updatedPages = prev.pages.map((page) => {
          if (page && page.data) {
            return {
              ...page,
              data: page.data.map((comment) =>
                comment.comment_id === request.comment_id
                  ? { ...comment, content: request.content }
                  : comment,
              ),
            };
          }
          return page;
        });

        const updatedComments: InfiniteData<GetCommentsResponse> = {
          ...prev,
          pages: updatedPages,
        };

        queryClient.setQueryData(queryKey, updatedComments);
      }
    };

    updateCommentCache('top');

    if (
      queryClient.getQueryData(
        commentKey.getCommentList({
          character_id: request.character_id,
          sort_by: 'newest',
        }),
      )
    ) {
      updateCommentCache('newest');
    }
  };

  const updateCommentLike = (
    request: LikeCommentRequest,
    type: MutationType, // 'save' 또는 'delete'
  ) => {
    const updateCommentCache = (sortOrder: SortComment, type: MutationType) => {
      const queryKey = commentKey.getCommentList({
        character_id: request.character_id,
        sort_by: sortOrder,
      });

      const prev =
        queryClient.getQueryData<InfiniteData<GetCommentsResponse>>(queryKey);

      if (prev) {
        const updatedPages = prev.pages.map((page) => {
          if (page && page.data) {
            // 좋아요 수 업데이트
            const updatedComments = page.data.map((comment) =>
              comment.comment_id === request.comment_id
                ? {
                    ...comment,
                    like_count:
                      type === 'save'
                        ? comment.like_count + 1
                        : comment.like_count - 1,
                    is_liked: type === 'save' ? true : false, // 좋아요 상태 업데이트
                  }
                : comment,
            );

            // 추천순일 경우, 좋아요 수에 따라 다시 정렬
            const sortedComments =
              sortOrder === 'top'
                ? updatedComments.sort((a, b) => b.like_count - a.like_count) // 좋아요 순으로 정렬
                : updatedComments; // 최신순일 경우 그대로

            return {
              ...page,
              data: sortedComments,
            };
          }
          return page;
        });

        const updatedCommentsData: InfiniteData<GetCommentsResponse> = {
          ...prev,
          pages: updatedPages,
        };

        // 업데이트된 댓글 리스트를 캐시에 반영
        queryClient.setQueryData(queryKey, updatedCommentsData);
      } else {
        console.error('No cached data found for queryKey:', queryKey);
      }
    };

    // 추천순 캐시 업데이트 후 정렬
    updateCommentCache('top', type);

    // 최신순 캐시도 업데이트 (정렬 불필요)
    if (
      queryClient.getQueryData(
        commentKey.getCommentList({
          character_id: request.character_id,
          sort_by: 'newest',
        }),
      )
    ) {
      updateCommentCache('newest', type);
    }
  };

  return {
    updatePersonaDetailsTotalComment,
    updateCommentList,
    deleteCommentList,
    editCommentList,
    updateCommentLike,
  };
};

export default useUpdateCommentList;
